import { Inject, Injectable, InjectionToken, NgZone, Optional } from '@angular/core';
import { fromEvent, merge, Observable, Subject, Subscription, timer } from 'rxjs';
import { startWith, switchMap, throttleTime } from 'rxjs/operators';

export interface UserIdleConfig {
    // seconds without user activity after which onTimerStart() emits
    idle: number;
}

export const USER_IDLE_CONFIG = new InjectionToken<UserIdleConfig>('USER_IDLE_CONFIG');

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];
const DEFAULT_IDLE_SECONDS = 600;

// Replacement for the dormant angular-user-idle package, with the four methods the login component
// and AuthorizationGuard use: watching starts a timer that any user activity on the document
// restarts; when it runs out, onTimerStart() emits once and the callers log the user out.
// Activity events are observed outside the Angular zone so a mouse move does not trigger change
// detection; the emission itself is brought back into the zone.
@Injectable({ providedIn: 'root' })
export class UserIdleService {
    private readonly timerStart$ = new Subject<number>();
    private readonly idleSeconds: number;
    private watch: Subscription | null = null;

    constructor(@Optional() @Inject(USER_IDLE_CONFIG) config: UserIdleConfig | null, private zone: NgZone) {
        this.idleSeconds = config?.idle ?? DEFAULT_IDLE_SECONDS;
    }

    startWatching(): void {
        if (this.watch) {
            return;
        }

        this.watch = this.zone.runOutsideAngular(() =>
            merge(...ACTIVITY_EVENTS.map((event) => fromEvent(document, event, { passive: true })))
                .pipe(
                    throttleTime(1000),
                    startWith(null),
                    switchMap(() => timer(this.idleSeconds * 1000))
                )
                .subscribe(() => this.zone.run(() => this.timerStart$.next(this.idleSeconds)))
        );
    }

    stopWatching(): void {
        this.watch?.unsubscribe();
        this.watch = null;
    }

    resetTimer(): void {
        if (this.watch) {
            this.stopWatching();
            this.startWatching();
        }
    }

    onTimerStart(): Observable<number> {
        return this.timerStart$.asObservable();
    }
}
